import { Skeleton } from '@mui/material';
import { useEffect, useState } from 'react';

import { useStreamActivityContext } from 'shared/features/posts';
import { useCanCreateHomePost } from 'shared/hooks';
import { CommentEnrichedReaction } from 'shared/types';

import { HomeFeedCardCommentButton } from './HomeFeedCardCommentButton';
import { HomeFeedCardComments } from './HomeFeedCardComments';
import { HomeFeedCardLikeButton } from './HomeFeedCardLikeButton';
import { Container, Reactions } from './HomeFeedCardReactions.styles';

interface Props {
  /**
   * Shows the available comments for the post.
   * Can be overridden by [the user permissions](https://github.com/jebelapp/jebel/issues/1528#issuecomment-2329497938).
   */
  withComments: boolean;
  /**
   * Shows the available reactions for the post.
   * Can be overridden by [the user permissions](https://github.com/jebelapp/jebel/issues/1528#issuecomment-2329497938).
   */
  withLikes: boolean;
}

export function HomeFeedCardReactions(props: Props) {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState<CommentEnrichedReaction[]>([]);

  const { data: activity, loading } = useStreamActivityContext();

  // Prevent showing the comments section if the user doesn't have the permission to create a post.
  // Useful with Business users that can't create posts.
  // https://github.com/jebelapp/jebel/issues/1528#issuecomment-2329497938
  const hasPermission = useCanCreateHomePost();

  const showSectionComments = hasPermission && props.withComments && showComments;
  const showSectionLikes = hasPermission && props.withLikes;

  const showSections = showComments || showSectionLikes;

  useEffect(() => {
    const comments = activity?.latest_reactions?.comment ?? [];
    setComments(comments);
  }, [activity]);

  const toggleComments = () => {
    setShowComments(prev => !prev);
  };

  if (!showSections) {
    return null;
  }

  if (loading || !activity) {
    return (
      <Container>
        <Reactions>
          <Skeleton variant="rectangular" height="2rem" width="4rem" />
          <Skeleton variant="rectangular" height="2rem" width="4rem" />
        </Reactions>
      </Container>
    );
  }

  return (
    <Container>
      <Reactions>
        {props.withLikes && <HomeFeedCardLikeButton />}

        {props.withComments && (
          <HomeFeedCardCommentButton comments={comments} onClick={toggleComments} />
        )}
      </Reactions>

      {showSectionComments && (
        <HomeFeedCardComments comments={comments} onCommentsChange={setComments} />
      )}
    </Container>
  );
}
